import { DatePickerField, InputField } from '../../../base/form';
import { FormField } from '../../../form-field';
import { Field, Formik, FormikProps } from 'formik';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActionMeta, MultiValue } from 'react-select';
import { ClaimEditorStore } from '../../../../stores/claim-editor';
import { AdditionalInformation } from '../../../../stores/claim-editor/claim-editor.types';
import { BaseInput } from '../../../base/input';
import { BaseSelect, getOptions, ISelectOption } from '../../../base/select';
import { BaseButton } from '../../../base/button';
import { OtherDateType } from '../../../../api';
import * as Yup from 'yup';
import * as S from './additional-information-form.styles';
import { FormErrorBanner } from '../../../base/form/form-error-banner';

const AdditionalInformationSchema = Yup.object().shape({
    controlNumber: Yup.string()
        .required('Control number is required')
        .max(20, 'Control number must be at most 20 characters'),
    admissionDate: Yup.string(),
    dischargeDate: Yup.string(),
    primaryPayerAuthorizationNumber: Yup.string(),
    secondaryPayerAuthorizationNumber: Yup.string(),
    tertiaryPayerAuthorizationNumber: Yup.string(),
    claimNarrative: Yup.string(),
    otherDateType: Yup.string(),
    fromOtherDate: Yup.string(),
    throughOtherDate: Yup.string(),
});

const initialValues: AdditionalInformation = {
    controlNumber: '',
    admissionDate: '',
    dischargeDate: '',
    primaryPayerAuthorizationNumber: '',
    secondaryPayerAuthorizationNumber: '',
    tertiaryPayerAuthorizationNumber: '',
    claimNarrative: '',
    otherDateType: '',
    fromOtherDate: '',
    throughOtherDate: '',
};

const OTHER_DATE_TYPE_OPTIONS: ISelectOption[] = [{ value: '', label: 'Select an option' }, ...getOptions(OtherDateType)];

interface IAdditionalInformationFormProps {
    claimEditorStore: ClaimEditorStore;
    isEditMode?: boolean;
}

export const AdditionalInformationForm: React.FunctionComponent<IAdditionalInformationFormProps> = observer(
    props => {
        const { claimEditorStore, isEditMode = false } = props;
        const saveTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);
        const lastCheckedControlNumberRef = useRef<string>('');
        const isControlNumberUnique = claimEditorStore?.isControlNumberUnique;

        // Convert store data to form values
        // Note: Only depends on activePayload, NOT on additionalInformation from store
        // This prevents reinitialization when auto-save updates the store (which would reset submitCount)
        // Pattern matches patient-form: reads from store OR payload fields, but only reacts to payload changes
        const initialFormValues = useMemo((): AdditionalInformation => {
            const payload = (claimEditorStore?.activePayload as any) || {};
            // Use saved data from store OR build from payload fields (same pattern as patient-form uses patient)
            const saved = claimEditorStore?.additionalInformation;
            
            if (saved) {
                return { ...saved };
            }
            // Fallback: build from payload fields (activePayload has fields at top level, not nested)
            return {
                ...initialValues,
                controlNumber: String(payload?.controlNumber || ''),
                claimNarrative: String(payload?.claimNarrative || ''),
                admissionDate: String(payload?.admissionDate || ''),
                dischargeDate: String(payload?.dischargeDate || ''),
                primaryPayerAuthorizationNumber: String(payload?.primaryPayerAuthorizationNumber || ''),
                secondaryPayerAuthorizationNumber: String(payload?.secondaryPayerAuthorizationNumber || ''),
                tertiaryPayerAuthorizationNumber: String(payload?.tertiaryPayerAuthorizationNumber || ''),
                otherDateType: String(payload?.otherDateType || ''),
                fromOtherDate: String(payload?.fromOtherDate || ''),
                throughOtherDate: String(payload?.throughOtherDate || ''),
            };

        }, [claimEditorStore?.activePayload]);

        // Debounced save to store
        const debouncedSave = useCallback(
            (values: AdditionalInformation) => {
                if (saveTimerRef.current) {
                    clearTimeout(saveTimerRef.current);
                }

                saveTimerRef.current = setTimeout(() => {
                    claimEditorStore?.updateAdditionalInformation(values);
                }, 500);
            },
            [claimEditorStore],
        );

        // Generate unique 20-character string
        const generateUniqueNumber = useCallback(
            (formik: FormikProps<AdditionalInformation>) => {
                const base = Math.random().toString(36).slice(2) + Date.now().toString(36);
                const randomString = base.slice(0, 20);
                formik.setFieldValue('controlNumber', randomString);
            },
            [],
        );

        // Cleanup timer on unmount
        useEffect(() => {
            return () => {
                if (saveTimerRef.current) {
                    clearTimeout(saveTimerRef.current);
                }
            };
        }, []);

        useEffect(() => {
            lastCheckedControlNumberRef.current = '';
        }, [claimEditorStore]);

        return (
            <Formik<AdditionalInformation>
                initialValues={initialFormValues}
                validationSchema={AdditionalInformationSchema}
                onSubmit={() => {
                    // No-op, we auto-save
                }}
                enableReinitialize
                validateOnMount={false}
                validateOnChange={true}
                validateOnBlur={false}
            >
                {formik => {
                    const trimmedControlNumber = (formik.values.controlNumber || '').trim();
                    const hasControlNumber = trimmedControlNumber.length > 0;
                    const showDuplicateBanner = hasControlNumber && isControlNumberUnique === false;
                    const showUniqueBanner = hasControlNumber && isControlNumberUnique === true;

                    // Auto-save on value change - save even when invalid so store has current data
                    // Save whenever values change, not just when dirty, to ensure store stays in sync
                    useEffect(() => {
                        debouncedSave(formik.values);
                    }, [formik.values, debouncedSave]);

                    // Respond to global validation trigger
                    useEffect(() => {
                        if (claimEditorStore.validationSignal > 0) {
                            formik.submitForm();
                        }
                    }, [claimEditorStore.validationSignal]);

                    useEffect(() => {
                        if (lastCheckedControlNumberRef.current === trimmedControlNumber) {
                            return;
                        }
                        lastCheckedControlNumberRef.current = trimmedControlNumber;
                        claimEditorStore?.checkControlNumberUniqueness(trimmedControlNumber);
                    }, [claimEditorStore, trimmedControlNumber]);

                    return (
                        <S.FormContainer>
                            {/* Show Formik validation banner  */}
                            <FormErrorBanner submitCount={formik.submitCount} isValid={formik.isValid} errors={formik.errors} />
                            {showDuplicateBanner && (
                                <S.InfoBanner>
                                    <S.InfoIcon>i</S.InfoIcon>
                                    <S.InfoContent>
                                        <strong>Visit/Encounter Number Already Exists</strong> The provided
                                        visit/encounter number already exists on an active claim. Submitting this claim
                                        with that number will result in an update to the existing claim instead of
                                        creating a new one.
                                    </S.InfoContent>
                                </S.InfoBanner>
                            )}

                            {showUniqueBanner && (
                                <S.SuccessBanner>
                                    <S.SuccessIcon>✓</S.SuccessIcon>
                                    <S.InfoContent>
                                        <strong>Control Number Is Unique</strong> Submitting this claim will create a new
                                        claim record.
                                    </S.InfoContent>
                                </S.SuccessBanner>
                            )}

                            <S.FormGrid>
                                <S.FormField span={2}>
                                    <S.VisitNumberRow>
                                        <S.VisitNumberField>
                                            <S.Label required>Control Number</S.Label>
                                            <Field
                                                name="controlNumber"
                                                placeholder="Control Number"
                                                component={InputField}
                                            />
                                        </S.VisitNumberField>
                                        <S.VisitNumberActions>
                                            <BaseButton
                                                type="button"
                                                variant="primary"
                                                onClick={() => generateUniqueNumber(formik)}
                                            >
                                                Generate Unique Number
                                            </BaseButton>
                                        </S.VisitNumberActions>
                                    </S.VisitNumberRow>
                                </S.FormField>

                                <S.FormField>
                                    <FormField label="Admission Date">
                                        <Field
                                            name="admissionDate"
                                            component={DatePickerField}
                                            placeholder="MMM D, YYYY"
                                        />
                                    </FormField>
                                </S.FormField>

                                <S.FormField>
                                    <FormField label="Discharge Date">
                                        <Field
                                            name="dischargeDate"
                                            component={DatePickerField}
                                            placeholder="MMM D, YYYY"
                                        />
                                    </FormField>
                                </S.FormField>

                                <S.FormField>
                                    <S.Label>Primary Prior Authorization Number</S.Label>
                                    <BaseInput
                                        type="text"
                                        name="primaryPayerAuthorizationNumber"
                                        placeholder="Primary Prior Authorization Number"
                                        value={formik.values.primaryPayerAuthorizationNumber}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                </S.FormField>

                                <S.FormField>
                                    <S.Label>Secondary Prior Authorization Number</S.Label>
                                    <BaseInput
                                        type="text"
                                        name="secondaryPayerAuthorizationNumber"
                                        placeholder="Secondary Prior Authorization Number"
                                        value={formik.values.secondaryPayerAuthorizationNumber}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                </S.FormField>

                                <S.FormField>
                                    <S.Label>Tertiary Prior Authorization Number</S.Label>
                                    <BaseInput
                                        type="text"
                                        name="tertiaryPayerAuthorizationNumber"
                                        placeholder="Tertiary Prior Authorization Number"
                                        value={formik.values.tertiaryPayerAuthorizationNumber}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                </S.FormField>

                                <S.FormField span={4}>
                                    <S.Label>Claim Narrative</S.Label>
                                    <BaseInput
                                        type="text"
                                        name="claimNarrative"
                                        placeholder="Claim Narrative"
                                        value={formik.values.claimNarrative}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                </S.FormField>

                                <S.FormField>
                                    <S.Label>Other Date Type</S.Label>
                                    <BaseSelect
                                        options={OTHER_DATE_TYPE_OPTIONS}
                                        value={OTHER_DATE_TYPE_OPTIONS.find(opt => opt.value === formik.values.otherDateType)}
                                        onChange={(
                                            newValue: ISelectOption | MultiValue<ISelectOption> | null,
                                            actionMeta: ActionMeta<ISelectOption>
                                        ) => {
                                            const option = newValue as ISelectOption | null;
                                            const value = option?.value as string || '';
                                            formik.setFieldValue('otherDateType', value);
                                        }}
                                        placeholder="Select an option"
                                    />
                                </S.FormField>

                                <S.FormField>
                                    <FormField label="From Other Date">
                                        <Field
                                            name="fromOtherDate"
                                            component={DatePickerField}
                                            placeholder="MMM D, YYYY"
                                        />
                                    </FormField>
                                </S.FormField>

                                <S.FormField>
                                    <FormField label="Through Other Date">
                                        <Field
                                            name="throughOtherDate"
                                            component={DatePickerField}
                                            placeholder="MMM D, YYYY"
                                        />
                                    </FormField>
                                </S.FormField>
                            </S.FormGrid>
                        </S.FormContainer>
                    );
                }}
            </Formik>
        );
    },
);

